import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Award, ClipboardCheck, Users, Building2, Gauge, Crown, Layers, AlertTriangle,
  Sparkles, Handshake, TrendingUp, ChevronRight, Play, Presentation,
} from "lucide-react";

const GOLD = "#C99A2E";
const PURPLE = "#7B2FBE";
const GREEN = "#6BB88A";
const BLUE = "#4FA3D1";
const TEAL = "#2E7D6B";
const AMBER = "#E4A93C";
const RED = "#E06C6C";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Card = ({ title, accent, right, children, testId }) => (
  <div className="rounded-sm border" style={{ borderColor: accent + "40", background: "rgba(11,53,88,0.35)" }} data-testid={testId}>
    <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: accent + "25" }}>
      <div className="w-1 h-4 rounded-sm" style={{ background: accent }} />
      <div className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: accent }}>{title}</div>
      <div className="ml-auto">{right}</div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);
const Chip = ({ children, color = BLUE, filled }) => (
  <span className="inline-flex items-center rounded-sm px-1.5 py-0.5 font-mono uppercase text-[9.5px] tracking-[0.14em]"
        style={{ background: filled ? color : color + "20", color: filled ? "#071B2B" : color, border: "1px solid " + color + "45" }}>
    {children}
  </span>
);

const LIFECYCLE = ["draft","review","committee","cabinet_submission","cabinet_approval","published","assigned","in_progress","monitoring","evidence_submitted","verified","completed","closed","archived"];

export const CabinetExecutionEngine = () => {
  const [tab, setTab] = useState("decisions");
  const [decisions, setDecisions] = useState([]);
  const [tracker, setTracker] = useState(null);
  const [acc, setAcc] = useState(null);
  const [sc, setSc] = useState(null);
  const [pres, setPres] = useState(null);
  const [prg, setPrg] = useState(null);
  const [esc, setEsc] = useState(null);
  const [ai, setAi] = useState(null);
  const [br, setBr] = useState(null);
  const [gov, setGov] = useState(null);
  const [bench, setBench] = useState(null);
  const [form, setForm] = useState({
    title: "", resolution: "", owner_ministry: "Mines", priority: "high", risk_rating: "medium",
    national_importance: "high", expected_outcome: "", budget_allocation_usd: 0,
    implementation_deadline: new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10),
  });

  const load = async () => {
    try {
      const [d, t, a, s, p, pr, e, i, b, g, cb] = await Promise.all([
        axios.get(`${API}/cadastre360/phase-xvii/decisions`,               { headers: auth() }),
        axios.get(`${API}/cadastre360/phase-xvii/implementation-tracker`, { headers: auth() }),
        axios.get(`${API}/cadastre360/phase-xvii/ministry-accountability`,{ headers: auth() }),
        axios.get(`${API}/cadastre360/phase-xvii/delivery-scorecard`,     { headers: auth() }),
        axios.get(`${API}/cadastre360/phase-xvii/presidential-dashboard`, { headers: auth() }),
        axios.get(`${API}/cadastre360/phase-xvii/programme-delivery`,     { headers: auth() }),
        axios.get(`${API}/cadastre360/phase-xvii/escalations`,            { headers: auth() }),
        axios.get(`${API}/cadastre360/phase-xvii/ai-delivery-intelligence`,{ headers: auth() }),
        axios.get(`${API}/cadastre360/phase-xvii/benefits-realisation`,   { headers: auth() }),
        axios.get(`${API}/cadastre360/phase-xvii/governance-twin`,        { headers: auth() }),
        axios.get(`${API}/cadastre360/phase-xvii/cabinet-benchmarking`,   { headers: auth() }),
      ]);
      setDecisions(d.data.items || []); setTracker(t.data); setAcc(a.data); setSc(s.data);
      setPres(p.data); setPrg(pr.data); setEsc(e.data); setAi(i.data); setBr(b.data);
      setGov(g.data); setBench(cb.data);
    } catch (e) { toast.error("Load failed — " + (e.response?.data?.detail || e.message)); }
  };
  useEffect(() => { load(); }, []);

  const submitDecision = async () => {
    if (!form.title || !form.resolution) { toast.error("Title & resolution required"); return; }
    try {
      await axios.post(`${API}/cadastre360/phase-xvii/decisions`, form, { headers: auth() });
      setForm({ ...form, title: "", resolution: "" });
      toast.success("Cabinet decision recorded (draft)");
      load();
    } catch (e) { toast.error("Failed — " + e.message); }
  };

  const transition = async (id, to_state) => {
    try {
      await axios.post(`${API}/cadastre360/phase-xvii/decisions/${id}/lifecycle`,
        { to_state, note: `Transitioned to ${to_state}` }, { headers: auth() });
      toast.success(`→ ${to_state}`);
      load();
    } catch (e) { toast.error("Failed — " + e.message); }
  };

  const TABS = [
    { key: "decisions",   label: "Decisions",             icon: Award,         accent: PURPLE },
    { key: "tracker",     label: "Implementation Tracker",icon: ClipboardCheck,accent: BLUE },
    { key: "ministry",    label: "Ministry Accountability",icon: Users,        accent: GOLD },
    { key: "scorecard",   label: "Delivery Scorecard",    icon: Gauge,         accent: GREEN },
    { key: "presidential",label: "Presidential Dashboard",icon: Crown,         accent: GOLD },
    { key: "programmes",  label: "Programme Delivery",    icon: Layers,        accent: PURPLE },
    { key: "escalations", label: "Escalations",           icon: AlertTriangle, accent: RED },
    { key: "ai",          label: "AI Delivery Intel",     icon: Sparkles,      accent: PURPLE },
    { key: "benefits",    label: "Benefits Realisation",  icon: Handshake,     accent: GREEN },
    { key: "governance",  label: "Governance Twin",       icon: Building2,     accent: BLUE },
    { key: "bench",       label: "Cabinet Benchmarking",  icon: TrendingUp,    accent: GREEN },
    { key: "boardroom",   label: "Boardroom Mode",        icon: Presentation,  accent: GOLD },
  ];

  return (
    <div className="p-6 space-y-4" data-testid="page-cabinet-execution-engine">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: PURPLE + "60",
                     background: `linear-gradient(120deg, rgba(7,27,43,0.95) 0%, rgba(11,53,88,0.85) 45%, ${PURPLE}30 100%)` }}>
        <Award size={38} className="mt-1" style={{ color: PURPLE }} />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px]" style={{ color: PURPLE }}>
            Cadastre360™ · Phase XVII · Cabinet Decision Execution Engine™
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-[#F6F8FA] mt-1">
            From Cabinet Intent to National Delivery
          </h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-4xl leading-relaxed">
            Complete Cabinet decision lifecycle · programme auto-generation · ministry accountability ·
            delivery scorecard · presidential oversight · governance twin · benefits realisation.
            Zero regression · zero duplication.
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={"px-3 py-1.5 text-[11.5px] flex items-center gap-1.5 font-medium border-b-2 " +
                             (tab === t.key ? "" : "border-transparent text-[#7C93A5]")}
                  style={tab === t.key ? { borderColor: t.accent, color: "#F6F8FA" } : {}}
                  data-testid={`cee-tab-${t.key}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "decisions" && (
        <>
          <Card title="Record Cabinet Decision" accent={PURPLE} testId="cee-decision-form">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Decision title"
                     className="md:col-span-2 rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15" data-testid="cee-title" />
              <input value={form.owner_ministry} onChange={(e) => setForm({...form, owner_ministry: e.target.value})} placeholder="Owner ministry"
                     className="rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15" data-testid="cee-ministry" />
              <select value={form.priority} onChange={(e) => setForm({...form, priority: e.target.value})}
                      className="rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15">
                {["low","medium","high","critical"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <input value={form.resolution} onChange={(e) => setForm({...form, resolution: e.target.value})} placeholder="Resolution"
                     className="md:col-span-3 rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15" data-testid="cee-resolution" />
              <input value={form.expected_outcome} onChange={(e) => setForm({...form, expected_outcome: e.target.value})} placeholder="Expected outcome"
                     className="md:col-span-3 rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15" />
              <input type="number" value={form.budget_allocation_usd} onChange={(e) => setForm({...form, budget_allocation_usd: Number(e.target.value)})} placeholder="Budget USD"
                     className="rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15" />
              <input type="date" value={form.implementation_deadline} onChange={(e) => setForm({...form, implementation_deadline: e.target.value})}
                     className="rounded-sm border bg-black/30 px-2 py-1.5 text-[12px] text-[#F6F8FA] border-white/15" />
              <button onClick={submitDecision} className="rounded-sm border px-3 py-1.5 text-[11.5px]"
                      style={{ borderColor: PURPLE + "60", color: "#F6F8FA", background: PURPLE + "25" }} data-testid="btn-record-decision-xvii">
                <Sparkles size={12} className="inline mr-1" /> Record decision
              </button>
            </div>
          </Card>
          <Card title={`Cabinet Decisions · ${decisions.length}`} accent={PURPLE} testId="cee-decisions-list">
            <div className="space-y-1.5 max-h-[520px] overflow-auto">
              {decisions.map((d) => (
                <div key={d.id} className="rounded-sm border p-2" style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }} data-testid={`cee-decision-${d.id}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono text-[10.5px] text-[#B3C2CC]">{d.decision_number}</span>
                    <div className="text-[12.5px] font-semibold text-[#F6F8FA] flex-1">{d.title}</div>
                    <Chip color={PURPLE}>{d.current_status}</Chip>
                    <Chip color={d.priority === "critical" ? RED : d.priority === "high" ? AMBER : GREEN}>{d.priority}</Chip>
                    <span className="text-[10.5px] text-[#B3C2CC] font-mono">{d.completion_pct}%</span>
                  </div>
                  <div className="text-[10.5px] text-[#B3C2CC]">{d.owner_ministry} · Budget USD {(d.budget_allocation_usd || 0).toLocaleString()} · Deadline {d.implementation_deadline?.slice(0, 10) || "—"}</div>
                  {d.ai_executive_commentary && <div className="text-[10.5px] text-[#7C93A5] italic mt-1">{d.ai_executive_commentary}</div>}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {LIFECYCLE.map((s) => (
                      <button key={s} onClick={() => transition(d.id, s)}
                              className="rounded-sm border px-1.5 py-0.5 text-[9.5px]"
                              style={{ borderColor: (d.current_status === s ? PURPLE : "rgba(255,255,255,0.15)"),
                                        background: d.current_status === s ? PURPLE + "25" : "rgba(7,27,43,0.55)",
                                        color: d.current_status === s ? "#F6F8FA" : "#B3C2CC" }}
                              data-testid={`cee-transition-${s}-${d.id}`}>
                        {s.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {decisions.length === 0 && <div className="text-[11px] text-[#7C93A5]">No decisions yet.</div>}
            </div>
          </Card>
        </>
      )}

      {tab === "tracker" && tracker && (
        <Card title="National Implementation Tracker™" accent={BLUE} testId="cee-tracker">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            {[["Total decisions",tracker.total_decisions,BLUE],["Avg completion",tracker.avg_completion_pct+"%",GREEN],["Delayed",tracker.delayed_decisions,RED],["Total budget","$"+tracker.total_budget_usd.toLocaleString(),GOLD]].map(([l,v,c]) => (
              <div key={l} className="rounded-sm border px-2 py-1.5" style={{ borderColor: c+"40", background: "rgba(7,27,43,0.55)" }}>
                <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5]">{l}</div>
                <div className="text-[15px] font-bold text-[#F6F8FA]" style={{ color: c }}>{v}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mb-1">By Lifecycle Stage</div>
              {Object.entries(tracker.by_lifecycle_stage).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 py-0.5 text-[11.5px]">
                  <div className="w-40 text-[#B3C2CC]">{k.replace(/_/g, ' ')}</div>
                  <div className="flex-1 h-1.5 rounded-sm bg-white/5"><div className="h-full rounded-sm" style={{ width: `${Math.min(v*10,100)}%`, background: BLUE }} /></div>
                  <div className="w-8 text-right font-mono text-[#F6F8FA]">{v}</div>
                </div>
              ))}
            </div>
            <div>
              <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mb-1">By Ministry</div>
              {Object.entries(tracker.by_ministry).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 py-0.5 text-[11.5px]">
                  <div className="w-40 text-[#B3C2CC]">{k}</div>
                  <div className="flex-1 h-1.5 rounded-sm bg-white/5"><div className="h-full rounded-sm" style={{ width: `${Math.min(v*20,100)}%`, background: GOLD }} /></div>
                  <div className="w-8 text-right font-mono text-[#F6F8FA]">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {tab === "ministry" && acc && (
        <Card title="Ministry Accountability Centre™" accent={GOLD} testId="cee-ministry">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[600px] overflow-auto">
            {acc.ministries.map((m) => (
              <div key={m.ministry} className="rounded-sm border p-2" style={{ borderColor: GOLD + "35", background: "rgba(7,27,43,0.55)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-[12.5px] font-semibold text-[#F6F8FA] flex-1">Ministry of {m.ministry}</div>
                  <Chip color={GREEN}>{m.implementation_pct}%</Chip>
                </div>
                <div className="text-[10.5px] text-[#B3C2CC]">Assigned {m.assigned_decisions} · Outstanding {m.outstanding_actions} · Budget {m.budget_status}</div>
                <div className="text-[10.5px] text-[#7C93A5]">Minister {m.minister_performance} · PS {m.permanent_secretary} · KPIs {m.kpi_performance}</div>
                <div className="text-[10.5px] text-[#B3C2CC] italic mt-1">{m.executive_feedback}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "scorecard" && sc && (
        <Card title={`Cabinet Delivery Scorecard · overall ${sc.overall}`} accent={GREEN} testId="cee-scorecard">
          {sc.scorecard.map((s) => (
            <div key={s.key} className="flex items-center gap-2 py-1 text-[12px]">
              <div className="w-52 text-[#F6F8FA]">{s.label}</div>
              <div className="flex-1 h-1.5 rounded-sm bg-white/5"><div className="h-full rounded-sm" style={{ width: `${s.value}%`, background: GREEN }} /></div>
              <div className="w-10 text-right font-mono text-[11px] text-[#F6F8FA]">{s.value}</div>
            </div>
          ))}
        </Card>
      )}

      {tab === "presidential" && pres && (
        <Card title="Presidential Delivery Dashboard™" accent={GOLD} testId="cee-presidential">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            {[["National Revenue","$"+pres.national_revenue_usd.toLocaleString(),GOLD],
              ["Cabinet Decisions",pres.cabinet_decisions_total,PURPLE],
              ["Critical Delays",pres.critical_delays,RED],
              ["Investment Pipeline","$"+(pres.investment_pipeline_usd/1_000_000_000).toFixed(2)+"B",GREEN],
              ["Land",pres.land_administration.toLocaleString()+" parcels",GOLD],
              ["Mining",pres.mineral_performance+" active",GOLD],
              ["Strategic Projects",pres.strategic_projects,BLUE],
              ["Dev Partner Projects",pres.development_partner_projects,BLUE]].map(([l,v,c]) => (
                <div key={l} className="rounded-sm border px-2 py-1.5" style={{ borderColor: c+"40", background: "rgba(7,27,43,0.55)" }}>
                  <div className="font-mono uppercase text-[9.5px] tracking-[0.16em] text-[#7C93A5]">{l}</div>
                  <div className="text-[15px] font-bold text-[#F6F8FA]" style={{ color: c }}>{v}</div>
                </div>
              ))}
          </div>
          <div className="rounded-sm border p-2" style={{ borderColor: PURPLE + "30", background: "rgba(7,27,43,0.5)" }}>
            <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mb-1">AI Presidential Brief</div>
            <div className="text-[12px] text-[#F6F8FA]">{pres.ai_presidential_brief}</div>
          </div>
          <div className="mt-2 text-[11px] text-[#DCE3E8]"><b>Health:</b> {pres.cabinet_health.label} ({pres.cabinet_health.score})</div>
          <div className="text-[11px] text-[#DCE3E8]"><b>Top priorities:</b> {pres.top_national_priorities.join(", ")}</div>
        </Card>
      )}

      {tab === "programmes" && prg && (
        <Card title="National Programme Delivery Centre™ · 17 sectors" accent={PURPLE} testId="cee-programmes">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {prg.sectors.map((s) => (
              <div key={s.sector} className="rounded-sm border p-2" style={{ borderColor: PURPLE + "35", background: "rgba(7,27,43,0.55)" }}>
                <div className="flex items-center gap-2"><div className="text-[12.5px] font-semibold text-[#F6F8FA] flex-1">{s.sector}</div><Chip color={GREEN}>{s.completion_pct}%</Chip></div>
                <div className="text-[10.5px] text-[#B3C2CC]">Programmes {s.programmes} · Budget {s.budget_utilisation}% · Provinces {s.provinces_covered} · Risks {s.risks}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "escalations" && esc && (
        <Card title={`Executive Escalation Engine™ · ${esc.count} active`} accent={RED} testId="cee-escalations">
          {esc.escalations.length === 0 && <div className="text-[11.5px] text-[#7C93A5]">No active escalations.</div>}
          <div className="space-y-1.5">
            {esc.escalations.map((e) => (
              <div key={e.decision_id} className="rounded-sm border p-2" style={{ borderColor: RED + "35", background: "rgba(7,27,43,0.5)" }}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="text-[12.5px] font-semibold text-[#F6F8FA] flex-1">{e.decision_title}</div>
                  <Chip color={e.severity === "critical" ? RED : AMBER} filled>{e.severity}</Chip>
                </div>
                <div className="text-[10.5px] text-[#B3C2CC]">Reasons: {e.reasons.join(", ")}</div>
                <div className="text-[10.5px] text-[#7C93A5]">Recipients: {e.recipients.join(" · ")}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "ai" && ai && (
        <Card title="AI Cabinet Delivery Intelligence™" accent={PURPLE} testId="cee-ai">
          <div className="text-[13px] text-[#F6F8FA] mb-2">{ai.implementation_summary}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div><div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mb-1">Executive recommendations</div>
              <ul className="text-[11.5px] text-[#F6F8FA] list-disc pl-4">{ai.executive_recommendations.map((r) => <li key={r}>{r}</li>)}</ul></div>
            <div><div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mb-1">Priority interventions</div>
              <ul className="text-[11.5px] text-[#F6F8FA] list-disc pl-4">{ai.priority_interventions.map((r) => <li key={r}>{r}</li>)}</ul></div>
          </div>
          <div className="mt-2 text-[11px] text-[#DCE3E8]"><b>Forecast (12 mo):</b> {ai.performance_forecast["12_months"]} · <b>Budget:</b> {ai.budget_analysis}</div>
        </Card>
      )}

      {tab === "benefits" && br && (
        <Card title="National Benefits Realisation™" accent={GREEN} testId="cee-benefits">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {Object.entries(br).filter(([k]) => k !== "generated_at").map(([k, v]) => (
              <div key={k} className="rounded-sm border px-2 py-1.5" style={{ borderColor: GREEN + "40", background: "rgba(7,27,43,0.55)" }}>
                <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">{k.replace(/_/g, ' ')}</div>
                <div className="text-[13px] font-bold text-[#F6F8FA]">{typeof v === "number" ? v.toLocaleString() : String(v)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "governance" && gov && (
        <Card title="Executive Governance Digital Twin™" accent={BLUE} testId="cee-governance">
          <div className="space-y-1">
            {gov.hierarchy.map((h, i) => (
              <div key={h.level} className="flex items-center gap-2 py-1" style={{ paddingLeft: i * 8 }}>
                <ChevronRight size={11} style={{ color: BLUE }} />
                <div className="flex-1 text-[12.5px] text-[#F6F8FA]">{h.level}</div>
                <div className="font-mono text-[11.5px] text-[#B3C2CC]">{h.count.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "bench" && bench && (
        <Card title="Cabinet Performance Benchmarking™" accent={GREEN} testId="cee-bench">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            {[["Current Cabinet",bench.current_cabinet],["Vision 2030",bench.vision_2030],["NDS",bench.nds],["AU Agenda 2063",bench.au_agenda_2063],["UN SDGs",bench.un_sdgs],["International best",bench.international_best]].map(([l,v]) => (
              <div key={l} className="rounded-sm border px-2 py-1.5" style={{ borderColor: GREEN + "40", background: "rgba(7,27,43,0.55)" }}>
                <div className="font-mono uppercase text-[9.5px] text-[#7C93A5]">{l}</div>
                <div className="text-[15px] font-bold text-[#F6F8FA]">{v}</div>
              </div>
            ))}
          </div>
          <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mb-1">Regional governments</div>
          {bench.regional_governments.map((r) => (
            <div key={r.country} className="text-[11.5px] text-[#F6F8FA]">{r.country}: <span className="font-mono">{r.score}</span></div>
          ))}
          <div className="font-mono uppercase text-[9.5px] text-[#7C93A5] mt-2 mb-1">Historical trend</div>
          <div className="flex items-end gap-2 h-16">
            {bench.historical_performance.map((h) => (
              <div key={h.year} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-sm" style={{ background: GREEN, height: `${h.score}%` }} />
                <div className="text-[10px] text-[#B3C2CC]">{h.year}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {tab === "boardroom" && br && (
        <Card title="Executive Boardroom Mode™" accent={GOLD} testId="cee-boardroom">
          <div className="text-[12px] text-[#DCE3E8] mb-3">One-click Cabinet-quality presentation environment. All components reuse existing services.</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {["Live dashboard","Interactive GIS","AI Narration","Programme Tracking","Decision Status","Budget","KPIs","Risks","Cabinet Storyline","Meeting Pack","National Value Report","Executive Timeline","International Benchmarking","Live Alerts"].map((c) => (
              <div key={c} className="rounded-sm border px-2 py-1.5" style={{ borderColor: GOLD + "40", background: "rgba(7,27,43,0.55)" }}>
                <div className="text-[12px] text-[#F6F8FA]">{c}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <a href="/app" className="rounded-sm border px-3 py-1.5 text-[11.5px]"
               style={{ borderColor: GOLD + "70", color: "#071B2B", background: GOLD }} data-testid="btn-launch-boardroom">
              <Play size={12} className="inline mr-1" /> Launch Boardroom on Executive Dashboard
            </a>
          </div>
        </Card>
      )}

      <div className="text-[10.5px] text-[#7C93A5] font-mono uppercase tracking-[0.18em] pt-1">
        Cadastre360™ · Phase XVII · Cabinet Decision Execution Engine · strictly additive
      </div>
    </div>
  );
};

export default CabinetExecutionEngine;
